import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

type InfestationLevel = 'none' | 'low' | 'medium' | 'high';

interface PestInfestation {
  type: string;
  level: InfestationLevel;
  area?: string;
}

type InfestationLevels = {
  rats: PestInfestation;
  ants: PestInfestation;
  cockroach: PestInfestation;
  others: PestInfestation;
};

type PestData = {
  treatedPests: string[];
  infestationLevels: InfestationLevels;
};

const PestControlForm: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  
  const [treatedPests, setTreatedPests] = useState<string[]>([]);
  const [infestationLevels, setInfestationLevels] = useState<InfestationLevels>({
    rats: { type: 'Rats', level: 'none' },
    ants: { type: 'Ants', level: 'none' },
    cockroach: { type: 'Cockroach', level: 'none' },
    others: { type: 'Others', level: 'none' }
  });

  const handlePestTypeChange = (pestType: string) => {
    setTreatedPests(prev => 
      prev.includes(pestType)
        ? prev.filter(p => p !== pestType)
        : [...prev, pestType]
    );
  };

  const handleInfestationLevelChange = (
    pestKey: keyof InfestationLevels,
    level: InfestationLevel,
    area?: string
  ) => {
    setInfestationLevels(prev => ({
      ...prev,
      [pestKey]: {
        ...prev[pestKey],
        level,
        area: level === 'high' ? area : undefined
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const pestData: PestData = {
        treatedPests,
        infestationLevels
      };

      // Replace with your actual API endpoint
      await axios.post('/pest-control/submit', pestData);
      
      setNotification({
        type: 'success',
        message: 'Pest control findings submitted successfully!'
      });
      
      // Navigate back or clear form after successful submission
      setTimeout(() => {
        navigate(-1); // Go back to previous page
      }, 2000);
    } catch (error) {
      console.error('Error submitting pest control data:', error);
      setNotification({
        type: 'error',
        message: 'Failed to submit pest control findings. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Pest Control Findings</h1>
      
      {notification && (
        <div className={`mb-4 p-3 rounded ${notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {notification.message}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Treated Pests Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6 border border-red-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-4">
              The property has been treated for the control of the following pest(s):
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                'Subterranean termites',
                'Cockroaches',
                'Ants',
                'Fleas / Ticks / Bedbugs',
                'Mosquitoes',
                'Rats',
                'Flies',
                'Others'
              ].map((pest) => (
                <label key={pest} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={treatedPests.includes(pest)}
                    onChange={() => handlePestTypeChange(pest)}
                    className="form-checkbox h-4 w-4 text-red-600 border-red-400"
                  />
                  <span className="text-sm text-gray-700">{pest}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Infestation Levels Section */}
          <div className="border border-red-200 rounded-lg p-4">
            <h3 className="text-md font-semibold mb-4 text-red-600">Findings (Pest Problems):</h3>
            <div className="space-y-4">
              {Object.entries(infestationLevels).map(([key, pest]) => (
                <div key={key} className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                  <span className="w-32 text-sm text-gray-700">{pest.type} Infestation Level:</span>
                  <div className="flex flex-wrap items-center gap-4">
                    {['none', 'low', 'medium', 'high'].map((level) => (
                      <label key={level} className="flex items-center space-x-1">
                        <input
                          type="radio"
                          name={`${key}-level`}
                          checked={pest.level === level}
                          onChange={() => handleInfestationLevelChange(
                            key as keyof InfestationLevels,
                            level as InfestationLevel
                          )}
                          className="form-radio h-4 w-4 text-red-600 border-red-400"
                        />
                        <span className="text-sm text-gray-700 capitalize">{level}</span>
                      </label>
                    ))}
                    {pest.level === 'high' && (
                      <input
                        type="text"
                        placeholder="Specify Area"
                        value={pest.area || ''}
                        onChange={(e) => handleInfestationLevelChange(
                          key as keyof InfestationLevels,
                          'high',
                          e.target.value
                        )}
                        className="input text-sm py-1 px-2 border border-red-400 rounded"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Save Findings'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PestControlForm; 